<template>
  <div id="wrapper">
    <h2 class="text-center">{{ selected_county.label}}</h2>
    <h3 class="text-center">{{ selected_month.label }}</h3>
    <div class="row">
      <div class="text-right col-md-6">
        <v-select placeholder="Select a month"
                  aria-label="Selected Month"
                  :clearable="false"
                  :options="months"
                  @input="currentMonth"
                  class="datelist-style month"></v-select>
      </div>
      <div class="text-left col-md-6">
        <v-select placeholder="Select/search for a county"
                  aria-label="Selected County"
                  :clearable="false"
                  :options="county_list"
                  @input="currentCounty"
                  class="datelist-style county"></v-select>
      </div>
    </div>
    <div class="text-center weather-type">
      <div @click="weatherType" class="btns btn-group" role="group" aria-label="Show weather value or anomaly buttons">
        <button id="weather-values" type="button" class="btn btn-secondary">
          <font-awesome-icon icon="sun" /> Monthly Values</button>
        <button id="weather-anomalies" type="button" class="btn btn-secondary">
          <font-awesome-icon icon="thermometer-three-quarters" /> Departure from Avg</button>
      </div>
      <div v-show="!showValues">
        <p>Weather anomalies are calculated based on average values over a 30 year window. Therefore, using a
        different window will result in different anomaly values. Currently NOAA uses 1981-2010 as their window. You can
        compare the anomaly values it gives against earlier windows.</p>
        <ul class="anomalies">
          <li>
            <label for="a_1921">1921-1950
              <input id="a_1921" type="radio" value="1921-1950" v-model="anomaly_type" />
            </label>
          </li>
          <li>
            <label for="a_1951">1951-1980
              <input id="a_1951" type="radio" value="1951-1980" v-model="anomaly_type" />
            </label>
          </li>
          <li>
            <label for="a_1981">1981-2010
              <input id="a_1981" type="radio" value="1981-2010" v-model="anomaly_type" />
            </label>
          </li>
        </ul>
      </div>
    </div>
    <h3 class="text-center">Average Temperature {{ displayType }} (&#x2109;)</h3>
    <strip-chart strip-type="tmp"
                 :data-values="temp_data"
                 :month="selected_month.option"
                 :data-avgs="temp_avgs"
                 :value-type="weather_type"
                 :which-anomaly="anomaly_type"/>
    <h3 class="text-center">Maximum Temperature {{ displayType }} (&#x2109;)</h3>
    <strip-chart strip-type="tmax"
                 :data-values="tmax_data"
                 :month="selected_month.option"
                 :data-avgs="tmax_avgs"
                 :value-type="weather_type"
                 :which-anomaly="anomaly_type"/>
    <h3 class="text-center">Minimum Temperature {{ displayType }} (&#x2109;)</h3>
    <strip-chart strip-type="tmin"
                 :data-values="tmin_data"
                 :month="selected_month.option"
                 :data-avgs="tmin_avgs"
                 :value-type="weather_type"
                 :which-anomaly="anomaly_type"/>
    <h3 class="text-center">Precipitation {{ displayType }} (inches)</h3>
    <strip-chart strip-type="precip"
                 :data-values="precip_data"
                 :month="selected_month.option"
                 :data-avgs="precip_avgs"
                 :value-type="weather_type"
                 :which-anomaly="anomaly_type"/>

    <p class="text-center">If you have any questions or comments feel free to contact me via Twitter at @farrelldlfarrel,
      or email at farrelldlfarrell at gmail.com</p>
    <p class="text-center">All data courtesy of the <a href="ftp://ftp.ncdc.noaa.gov/pub/data/cirs/climdiv/">
      National Oceanic and Atmospheric Administration</a>.</p>
  </div>
</template>

<script>
  import stripChart from "./stripChart";
  import countyHelpers from "../mixins/countyHelpers";
  import * as d3 from "d3";

  export default {
    name: 'stripChartWrapper',

    components: {stripChart},

    mixins: [countyHelpers],

    data() {
      return {
        initial: true,
        temp_data: [],
        tmax_data: [],
        tmin_data: [],
        precip_data: [],
        temp_avgs: [],
        tmax_avgs: [],
        tmin_avgs: [],
        precip_avgs: [],
        weather_type: 'weather-values',
        selected_county: { option: '01001', label: 'Autauga County, AL' },
        county_list: [],
        anomaly_type: '1981-2010',
        selected_month: { option: 'mar', label: 'March'},
        months: [
          { option: 'jan', label: 'January'},
          { option: 'feb', label: 'February'},
          { option: 'mar', label: 'March'},
          { option: 'apr', label: 'April'},
          { option: 'may', label: 'May'},
          { option: 'jun', label: 'June'},
          { option: 'jul', label: 'July'},
          { option: 'aug', label: 'August'},
          { option: 'sep', label: 'September'},
          { option: 'oct', label: 'October'},
          { option: 'nov', label: 'November'},
          { option: 'dec', label: 'December'},
        ]
      }
    },

    computed: {
      fileList() {
        let files = [
          d3.csv(`static/counties/${this.selected_county.option}-tmpccy.csv`),
          d3.csv(`static/counties/${this.selected_county.option}-tmaxcy.csv`),
          d3.csv(`static/counties/${this.selected_county.option}-tmincy.csv`),
          d3.csv(`static/counties/${this.selected_county.option}-pcpncy.csv`),
          d3.csv(`static/counties-avgs/${this.selected_county.option}-tmpccy.csv`),
          d3.csv(`static/counties-avgs/${this.selected_county.option}-tmaxcy.csv`),
          d3.csv(`static/counties-avgs/${this.selected_county.option}-tmincy.csv`),
          d3.csv(`static/counties-avgs/${this.selected_county.option}-pcpncy.csv`),
        ];

        if (this.initial) {
          files.push(d3.json('static/county-list.json'));
        }

        return files;
      },

      showValues() {
        return this.weather_type === 'weather-values'
      },

      displayType() {
        return this.showValues ? 'Values' : 'Anomalies';
      }
    },

    methods: {
      load() {
        let vm = this;

        Promise.all(this.fileList).then((data) => {
          vm.temp_data = data[0];
          vm.tmax_data = data[1];
          vm.tmin_data = data[2];
          vm.precip_data = data[3];
          vm.temp_avgs = data[4];
          vm.tmax_avgs = data[5];
          vm.tmin_avgs = data[6];
          vm.precip_avgs = data[7];
          vm.county_list = data[8];
          vm.initial = false;
        });
      },

      reload() {
        let vm = this;

        Promise.all(this.fileList).then((data) => {
          vm.temp_data = data[0];
          vm.tmax_data = data[1];
          vm.tmin_data = data[2];
          vm.precip_data = data[3];
          vm.temp_avgs = data[4];
          vm.tmax_avgs = data[5];
          vm.tmin_avgs = data[6];
          vm.precip_avgs = data[7];
        });
      },

      currentCounty(val) {
        if (val !== '') {
          this.selected_county = val;
          this.reload();
        }
      },

      currentMonth(val) {
        if (val !== '') {
          this.selected_month = val;
        }
      },

      weatherType(e) {
        this.weather_type = e.target.id;
      }
    },

    created() {
      this.load();
    }
  }
</script>

<style scoped>
  a {
    color: orange;
  }

  .month {
    margin-right: 0;
  }

  .county {
    margin-left: 0;
  }

  .weather-type {
    margin-bottom: 25px;
  }

  .anomalies {
    margin-top: 15px;
  }

  .anomalies li {
    display: inline-block;
    padding-right: 10px;
  }

  @media (max-width: 767px)  {
    .datelist-style {
      margin: 5px auto;
    }

    .month {
      margin-right: auto;
    }

    .county {
      margin-bottom: 25px;
      margin-left: auto;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/stripChartWrapper.vue